import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { fmtAmount } from "@/lib/currency";
import { SignaturePad } from "@/components/SignaturePad";

const STEPS = [
  { key: "consent",  label: "E-SIGN Consent" },
  { key: "otp",      label: "Identity Verification" },
  { key: "review",   label: "Review Documents" },
  { key: "sign",     label: "Sign" },
];

export default function PortalCombinedAcceptSign() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, refresh } = usePortalAuth();

  const [pkg, setPkg]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  // step: "consent" | "otp" | "review" | "sign" | "done"
  const [step, setStep] = useState("consent");
  const [activeTab, setActiveTab] = useState("estimate");

  // E-SIGN consent
  const [consented, setConsented]   = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);

  // OTP
  const [otpSent, setOtpSent]   = useState(false);
  const [otpCode, setOtpCode]   = useState("");
  const [otpBusy, setOtpBusy]   = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  // Signature
  const [sig, setSig] = useState({ method: "typed", drawnImage: null, typedName: "" });
  const [acknowledge, setAcknowledge] = useState(false);
  const [signBusy, setSignBusy] = useState(false);

  // Result
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await portalApi.get(`/portal/combined/${id}`);
      setPkg(data);
      setSig((s) => ({ ...s, typedName: profile?.full_name || "" }));
      // Restore server-side state
      const ua = data.ua_status || {};
      if (ua.engagement_status === "signed" || ua.engagement_status === "active") {
        setStep("done");
        setResult({ idempotent: true });
      } else if (ua.otp_verified && ua.esign_consented) {
        setStep("review");
        setOtpVerified(true);
      } else if (ua.esign_consented) {
        setStep("otp");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "This acceptance package is unavailable.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id, profile]);

  useEffect(() => { load(); }, [load]);

  // --- Step 1: E-SIGN Consent ---
  const submitConsent = async () => {
    if (!consented) return toast.error("Please confirm E-SIGN consent to continue.");
    setConsentBusy(true);
    try {
      await portalApi.post(`/portal/engagements/${id}/ua/esign-consent`, {
        consented: true,
        consent_language_version: "ESIGN-DISCLOSURE-v1",
      });
      setStep("otp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record consent. Please try again.");
    } finally {
      setConsentBusy(false);
    }
  };

  // --- Step 2a: Request OTP ---
  const requestOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await portalApi.post(`/portal/engagements/${id}/ua/request-otp`);
      setMaskedEmail(data.masked_email || "your email");
      setOtpSent(true);
      toast.success("Verification code sent!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send verification code.");
    } finally {
      setOtpBusy(false);
    }
  };

  // --- Step 2b: Verify OTP ---
  const verifyOtp = async () => {
    if (otpCode.trim().length < 4) return toast.error("Please enter the verification code.");
    setOtpBusy(true);
    try {
      await portalApi.post(`/portal/engagements/${id}/ua/verify-otp`, { otp: otpCode.trim() });
      setOtpVerified(true);
      setStep("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid or expired code. Please try again.");
    } finally {
      setOtpBusy(false);
    }
  };

  // --- Step 4: Combined Accept + Sign ---
  const submitSign = async () => {
    if (!acknowledge) return toast.error("Please confirm acknowledgement to sign.");
    const sigValid = sig.method === "drawn" ? !!sig.drawnImage : !!sig.typedName.trim();
    if (!sigValid) return toast.error(sig.method === "drawn" ? "Please draw your signature." : "Please type your full legal name.");
    setSignBusy(true);
    try {
      const { data } = await portalApi.post(`/portal/engagements/${id}/combined-accept-sign`, {
        typed_signature: sig.method === "typed" ? sig.typedName.trim() : (profile?.full_name || ""),
        otp_verified: true,
        esign_consented: true,
        scroll_completed: true,
        signature_method: sig.method === "drawn" ? "drawn" : "typed",
        signature_image_base64: sig.method === "drawn" ? sig.drawnImage : null,
      });
      setResult(data);
      setStep("done");
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete signing. Please try again.");
    } finally {
      setSignBusy(false);
    }
  };

  // Progress bar step index
  const stepIdx = { consent: 0, otp: 1, review: 2, sign: 3, done: 3 }[step] ?? 0;

  if (loading) return (
    <div className="flex justify-center items-center min-h-[60vh]" data-testid="combined-sign-loading">
      <Icons.Loader2 className="h-8 w-8 animate-spin text-[#F5A623]" />
    </div>
  );
  if (error) return (
    <div className="max-w-xl mx-auto text-center py-20 px-4" data-testid="combined-sign-error">
      <Icons.AlertTriangle className="h-10 w-10 text-[#F5A623] mx-auto mb-3" />
      <h2 className="text-lg font-semibold text-[#171B2E] mb-2">Package unavailable</h2>
      <p className="text-slate-500 text-sm">{error}</p>
    </div>
  );

  const eng = pkg?.engagement || {};
  const est = pkg?.estimate || {};
  const currency = est.currency || "USD";
  const lineItems = est.line_items || [];

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="combined-accept-sign-page">
      <button onClick={() => navigate("/portal")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#171B2E] mb-5">
        <Icons.ArrowLeft className="h-4 w-4" />Back to dashboard
      </button>

      {/* Header */}
      <div className="bg-[#171B2E] rounded-2xl text-white px-6 py-5 mb-5" data-testid="combined-sign-header">
        <div className="text-xs text-[#F5A623] font-mono mb-0.5">{eng.engagement_number || "Engagement"}</div>
        <h1 className="font-heading text-xl font-bold leading-tight">Review and Sign — Estimate + Engagement Agreement</h1>
        <p className="text-sm text-slate-300 mt-1">Review both documents together and sign once to accept your estimate and execute your agreement.</p>
      </div>

      {/* Progress steps */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 mb-5" data-testid="combined-sign-progress">
        <div className="flex items-center">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors ${
                  i < stepIdx || (step === "done" && i <= 3) ? "bg-emerald-500 border-emerald-500 text-white"
                  : i === stepIdx && step !== "done" ? "bg-[#F5A623] border-[#F5A623] text-[#171B2E]"
                  : "bg-white border-slate-300 text-slate-400"
                }`}>
                  {i < stepIdx || step === "done" ? <Icons.Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span className={`mt-1 text-[11px] font-medium text-center leading-tight ${
                  i === stepIdx && step !== "done" ? "text-[#171B2E]" : i < stepIdx ? "text-emerald-600" : "text-slate-400"
                }`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-1.5 -mt-4 ${i < stepIdx ? "bg-emerald-400" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: E-SIGN Consent */}
      {step === "consent" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="esign-consent-step">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[#F5A623]/10 flex items-center justify-center">
              <Icons.FileText className="h-5 w-5 text-[#F5A623]" />
            </div>
            <div>
              <h2 className="font-semibold text-[#171B2E]">Electronic Signature Consent</h2>
              <p className="text-sm text-slate-500">Before proceeding, please confirm your consent to use electronic signatures.</p>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 leading-relaxed mb-5">
            <p className="font-semibold text-[#171B2E] mb-2">E-SIGN Disclosure — ESIGN-DISCLOSURE-v1</p>
            <p>By checking the box below, you consent to conduct business and enter into agreements electronically. Your electronic signature is legally binding and equivalent to a handwritten signature. You may withdraw consent at any time by contacting your advisor, although this may affect our ability to proceed.</p>
            <p className="mt-2">Both the approved Estimate and the Engagement Agreement will be presented electronically. Your single electronic signature will constitute acceptance of the Estimate and execution of the Engagement Agreement.</p>
          </div>
          <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-[#F5A623] cursor-pointer transition-colors mb-5">
            <input
              type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)}
              className="mt-0.5 accent-[#F5A623] h-4 w-4"
              data-testid="esign-consent-checkbox"
            />
            <span className="text-sm text-slate-700 font-medium">
              I have read and agree to use electronic records and signatures. I understand this is legally equivalent to a handwritten signature.
            </span>
          </label>
          <button
            onClick={submitConsent} disabled={!consented || consentBusy}
            className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-xl py-3 flex items-center justify-center gap-2 transition-opacity"
            data-testid="esign-consent-btn"
          >
            {consentBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.ArrowRight className="h-4 w-4" />}
            Continue to Identity Verification
          </button>
        </div>
      )}

      {/* Step 2: OTP Verification */}
      {step === "otp" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="otp-step">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[#F5A623]/10 flex items-center justify-center">
              <Icons.ShieldCheck className="h-5 w-5 text-[#F5A623]" />
            </div>
            <div>
              <h2 className="font-semibold text-[#171B2E]">Identity Verification</h2>
              <p className="text-sm text-slate-500">We'll send a one-time code to confirm your identity before signing.</p>
            </div>
          </div>
          {!otpSent ? (
            <>
              <p className="text-sm text-slate-600 mb-5">A verification code will be sent to your registered email address. Please have access to your email to continue.</p>
              <button
                onClick={requestOtp} disabled={otpBusy}
                className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-xl py-3 flex items-center justify-center gap-2"
                data-testid="otp-send-btn"
              >
                {otpBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}
                Send Verification Code
              </button>
            </>
          ) : (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-700 mb-5">
                <Icons.CheckCircle className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Code sent to {maskedEmail}
              </div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Enter 6-digit verification code</label>
              <input
                type="text" inputMode="numeric" maxLength={8}
                value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="w-full border border-slate-300 rounded-xl px-4 py-3 text-center text-xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-[#F5A623] mb-4"
                data-testid="otp-input"
                onKeyDown={(e) => e.key === "Enter" && verifyOtp()}
              />
              <button
                onClick={verifyOtp} disabled={otpBusy || otpCode.length < 4}
                className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-xl py-3 flex items-center justify-center gap-2 mb-3"
                data-testid="otp-verify-btn"
              >
                {otpBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.ShieldCheck className="h-4 w-4" />}
                Verify Code
              </button>
              <button onClick={requestOtp} disabled={otpBusy} className="w-full text-sm text-slate-500 hover:text-[#171B2E] py-2" data-testid="otp-resend-btn">
                Resend code
              </button>
            </>
          )}
        </div>
      )}

      {/* Step 3: Review Documents */}
      {step === "review" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="review-step">
          <div className="border-b border-slate-200 px-1 pt-1 flex gap-1 bg-slate-50">
            <button
              onClick={() => setActiveTab("estimate")}
              className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${
                activeTab === "estimate" ? "bg-white text-[#171B2E] border border-b-white border-slate-200 -mb-px" : "text-slate-500 hover:text-[#171B2E]"
              }`}
              data-testid="tab-estimate"
            >
              <Icons.Receipt className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Estimate
            </button>
            <button
              onClick={() => setActiveTab("engagement")}
              className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${
                activeTab === "engagement" ? "bg-white text-[#171B2E] border border-b-white border-slate-200 -mb-px" : "text-slate-500 hover:text-[#171B2E]"
              }`}
              data-testid="tab-engagement"
            >
              <Icons.FileText className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Engagement Agreement
            </button>
          </div>

          <div className="p-5">
            {/* Estimate Tab */}
            {activeTab === "estimate" && (
              <div data-testid="estimate-review-panel">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs text-slate-400 font-mono">{est.estimate_number}</div>
                    <h3 className="font-semibold text-[#171B2E] text-base">Approved Estimate</h3>
                  </div>
                  {est.total != null && (
                    <div className="text-right">
                      <div className="text-xs text-slate-400">Total</div>
                      <div className="text-xl font-bold text-[#171B2E]">{fmtAmount(est.total, currency)}</div>
                    </div>
                  )}
                </div>
                {est.scope_summary && (
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">{est.scope_summary}</p>
                )}
                {lineItems.length > 0 && (
                  <div className="border border-slate-200 rounded-xl overflow-hidden mb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-left">
                        <tr>
                          <th className="px-3 py-2 text-xs font-semibold text-slate-500">Service</th>
                          <th className="px-3 py-2 text-xs font-semibold text-slate-500 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2.5 text-slate-700">{li.description || li.service_code || "—"}</td>
                            <td className="px-3 py-2.5 text-slate-800 font-medium text-right">
                              {li.amount != null ? fmtAmount(li.amount, currency) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {est.total != null && (
                        <tfoot className="bg-slate-50 border-t border-slate-200">
                          <tr>
                            <td className="px-3 py-2 font-semibold text-[#171B2E]">Total</td>
                            <td className="px-3 py-2 font-bold text-[#171B2E] text-right">{fmtAmount(est.total, currency)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
                {est.payment_terms && (
                  <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600">
                    <span className="font-semibold text-slate-700">Payment terms: </span>{est.payment_terms}
                  </div>
                )}
                <button
                  onClick={() => setActiveTab("engagement")}
                  className="w-full mt-4 border border-[#171B2E] text-[#171B2E] font-semibold rounded-xl py-2.5 text-sm hover:bg-[#171B2E] hover:text-white transition-colors flex items-center justify-center gap-2"
                  data-testid="review-next-tab-btn"
                >
                  <Icons.ArrowRight className="h-4 w-4" />
                  Continue to Engagement Agreement
                </button>
              </div>
            )}

            {/* Engagement Agreement Tab */}
            {activeTab === "engagement" && (
              <div data-testid="engagement-review-panel">
                <div className="mb-3">
                  <div className="text-xs text-slate-400 font-mono">{eng.engagement_number}</div>
                  <h3 className="font-semibold text-[#171B2E] text-base">Engagement Agreement</h3>
                </div>
                <div
                  className="border border-slate-100 rounded-xl p-5 bg-slate-50/50 text-sm text-slate-700 leading-relaxed max-h-[50vh] overflow-y-auto tf-scroll mb-4"
                  data-testid="engagement-letter-body"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(eng.letter_body || "<p>Engagement agreement text loading…</p>") }}
                />
                <button
                  onClick={() => setStep("sign")}
                  className="w-full bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-semibold rounded-xl py-3 flex items-center justify-center gap-2 transition-colors"
                  data-testid="proceed-to-sign-btn"
                >
                  <Icons.PenTool className="h-4 w-4" />
                  I have reviewed both documents — proceed to sign
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 4: Sign */}
      {step === "sign" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="sign-step">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-full bg-[#F5A623]/10 flex items-center justify-center">
              <Icons.PenTool className="h-5 w-5 text-[#F5A623]" />
            </div>
            <div>
              <h2 className="font-semibold text-[#171B2E]">Electronic Signature</h2>
              <p className="text-sm text-slate-500">Your signature accepts the estimate and executes the engagement agreement.</p>
            </div>
          </div>

          {/* Document summary */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div className="text-xs text-slate-400 font-mono mb-0.5">{est.estimate_number}</div>
              <div className="text-sm font-semibold text-[#171B2E]">Estimate</div>
              {est.total != null && <div className="text-sm text-slate-600 mt-0.5">{fmtAmount(est.total, currency)}</div>}
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div className="text-xs text-slate-400 font-mono mb-0.5">{eng.engagement_number}</div>
              <div className="text-sm font-semibold text-[#171B2E]">Engagement Agreement</div>
              <div className="text-xs text-slate-500 mt-0.5">Ready to execute</div>
            </div>
          </div>

          <SignaturePad
            onChange={setSig}
            legalName={profile?.full_name || ""}
            label="Your Electronic Signature"
          />

          {/* Combined acknowledgement */}
          <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-[#F5A623] cursor-pointer transition-colors mt-4 mb-4" data-testid="combined-acknowledge-label">
            <input
              type="checkbox" checked={acknowledge} onChange={(e) => setAcknowledge(e.target.checked)}
              className="mt-0.5 accent-[#F5A623] h-4 w-4 shrink-0"
              data-testid="combined-acknowledge-checkbox"
            />
            <span className="text-sm text-slate-700 font-medium leading-relaxed">
              I have reviewed and accept the approved Estimate ({est.estimate_number}) and the full Engagement Agreement ({eng.engagement_number}) presented to me, and I agree to the Master Engagement Terms and Conditions incorporated therein. My electronic signature constitutes acceptance of the estimated charges and execution of the Engagement Agreement as a single legally binding action.
            </span>
          </label>

          <div className="flex gap-3">
            <button
              onClick={() => setStep("review")}
              className="px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="back-to-review-btn"
            >
              <Icons.ArrowLeft className="h-4 w-4 inline mr-1 -mt-0.5" />Back
            </button>
            <button
              onClick={submitSign}
              disabled={signBusy || !acknowledge}
              className="flex-1 bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-xl py-3 flex items-center justify-center gap-2 transition-opacity"
              data-testid="combined-sign-btn"
            >
              {signBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle className="h-4 w-4" />}
              Accept Estimate &amp; Sign Agreement
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Done */}
      {step === "done" && (
        <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-8 text-center" data-testid="combined-sign-done">
          <div className="h-16 w-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icons.CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-emerald-800 mb-2" data-testid="combined-sign-success-heading">
            {result?.idempotent ? "Already Complete" : "Signed — Thank You!"}
          </h2>
          <p className="text-slate-600 text-sm mb-6">
            {result?.idempotent
              ? "Your estimate has already been accepted and your engagement agreement is already signed and on file."
              : "Your estimate has been accepted and your engagement agreement is signed. A confirmation has been sent to your email."}
          </p>

          <div className="grid grid-cols-2 gap-3 text-left mb-6">
            {(result?.estimate_number || est.estimate_number) && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                <div className="text-xs text-slate-400 font-mono">{result?.estimate_number || est.estimate_number}</div>
                <div className="text-sm font-semibold text-[#171B2E]">Estimate</div>
                <div className="flex items-center gap-1 text-xs text-emerald-700 mt-0.5"><Icons.Check className="h-3 w-3" />Accepted</div>
              </div>
            )}
            {(result?.engagement_number || eng.engagement_number) && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                <div className="text-xs text-slate-400 font-mono">{result?.engagement_number || eng.engagement_number}</div>
                <div className="text-sm font-semibold text-[#171B2E]">Agreement</div>
                <div className="flex items-center gap-1 text-xs text-emerald-700 mt-0.5"><Icons.Check className="h-3 w-3" />Signed</div>
              </div>
            )}
          </div>

          {result?.invoice_id && (
            <div className="bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-xl p-4 mb-5 text-left">
              <div className="flex items-center gap-2">
                <Icons.CreditCard className="h-5 w-5 text-[#F5A623]" />
                <div className="font-semibold text-[#171B2E] text-sm">Invoice available</div>
              </div>
              <p className="text-sm text-slate-600 mt-1">An invoice is ready in your Secure Portal for review and payment.</p>
            </div>
          )}

          <button
            onClick={() => navigate("/portal")}
            className="w-full bg-[#171B2E] hover:bg-[#222842] text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 transition-colors"
            data-testid="combined-sign-portal-btn"
          >
            <Icons.Home className="h-4 w-4" />
            Go to your portal
          </button>
        </div>
      )}

      <p className="text-center text-xs text-slate-400 mt-6">Secured by Tax Flow · Your signature is legally binding on both documents</p>
    </div>
  );
}
